import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import BatteryIcon from "@mui/icons-material/BatteryFull";
import TuneIcon from "@mui/icons-material/Tune";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import React from "react";
import { observer } from "mobx-react";
import * as socketio from "socket.io-client";
import TimeAgo from "react-time-ago";
import DeviceControlsDialog from "../components/DeviceControlsDialog";
import { DeviceInfo, printerColorFromStatus } from "../components/Device";
import { BACKEND_URI } from "../constants";
import AuthStore from "../stores/AuthStore";
import Toaster from "../common/Toaster";

const DeviceEditor = observer(() => {
    const [devices, setDevices] = React.useState<DeviceInfo[]>([]);
    // Pending (unsaved) location edits, keyed by device identifier
    const [edits, setEdits] = React.useState<Map<number, string>>(new Map());
    const [selected, setSelected] = React.useState<Set<number>>(new Set());
    const [search, setSearch] = React.useState("");
    const [bulkLocation, setBulkLocation] = React.useState("");
    const [controlsOpen, setControlsOpen] = React.useState(false);
    const socket = React.useRef<socketio.Socket | null>(null);

    React.useEffect(() => {
        socket.current = socketio.io(BACKEND_URI, { auth: { token: AuthStore.tokenString } });
        socket.current.on("list", (list: DeviceInfo[]) => setDevices(list));
        return () => {
            socket.current?.disconnect();
        };
    }, []);

    const visibleDevices = devices
        .filter((it) => {
            if (!search) return true;
            const s = search.toLowerCase();
            return (
                it.identifier.toString().includes(s) ||
                (it.location ?? "").toLowerCase().includes(s) ||
                (it.user ?? "").toLowerCase().includes(s)
            );
        })
        .sort((a, b) => a.identifier - b.identifier);

    const knownLocations = Array.from(new Set(devices.map((it) => it.location).filter((it) => !!it))).sort((a, b) => a.localeCompare(b));

    const stageEdit = (identifier: number, location: string) => {
        const next = new Map(edits);
        const original = devices.find((it) => it.identifier === identifier);
        if ((original?.location ?? "") === location) {
            next.delete(identifier);
        } else {
            next.set(identifier, location);
        }
        setEdits(next);
    };

    const toggleSelected = (identifier: number) => {
        const next = new Set(selected);
        if (next.has(identifier)) {
            next.delete(identifier);
        } else {
            next.add(identifier);
        }
        setSelected(next);
    };

    const allVisibleSelected = visibleDevices.length > 0 && visibleDevices.every((it) => selected.has(it.identifier));

    const toggleSelectAll = () => {
        if (allVisibleSelected) {
            setSelected(new Set());
        } else {
            setSelected(new Set(visibleDevices.map((it) => it.identifier)));
        }
    };

    const onApplyBulkLocation = () => {
        const next = new Map(edits);
        selected.forEach((identifier) => {
            const original = devices.find((it) => it.identifier === identifier);
            if ((original?.location ?? "") === bulkLocation) {
                next.delete(identifier);
            } else {
                next.set(identifier, bulkLocation);
            }
        });
        setEdits(next);
    };

    const onSave = () => {
        edits.forEach((location, identifier) => {
            socket.current?.emit("update", { identifier, location });
        });
        Toaster.info(`Saved ${edits.size} device${edits.size === 1 ? "" : "s"}`);
        setEdits(new Map());
    };

    const onDiscard = () => {
        setEdits(new Map());
    };

    if (!AuthStore.hasAdmin) {
        return (
            <Container maxWidth="xl" sx={{ marginTop: "10px" }}>
                <Typography variant="h5">You need admin access to use the device editor.</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ marginTop: "10px" }}>
            <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="10px">
                <Typography variant="h4">Device Editor</Typography>
                <TextField
                    size="small"
                    label="Search POS # / location / user"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: "280px" }}
                />
            </Box>
            <Paper sx={{ padding: "10px", margin: "10px 0" }}>
                <Box display="flex" flexDirection="row" alignItems="center" flexWrap="wrap" gap="10px">
                    <Typography variant="body1" sx={{ minWidth: "110px" }}>
                        {selected.size} selected
                    </Typography>
                    <Autocomplete
                        freeSolo
                        size="small"
                        options={knownLocations}
                        inputValue={bulkLocation}
                        onInputChange={(e, value) => setBulkLocation(value)}
                        sx={{ width: "250px" }}
                        renderInput={(params) => <TextField {...params} label="Set location for selected" />}
                    />
                    <Button variant="contained" color="primary" disabled={selected.size === 0} onClick={onApplyBulkLocation}>
                        Apply
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={selected.size === 0}
                        onClick={() => setControlsOpen(true)}
                        startIcon={<TuneIcon />}
                    >
                        Controls
                    </Button>
                    <Divider orientation="vertical" flexItem />
                    <Typography variant="body1" color={edits.size > 0 ? "secondary" : "text.secondary"}>
                        {edits.size} unsaved change{edits.size === 1 ? "" : "s"}
                    </Typography>
                    <Button variant="contained" color="primary" disabled={edits.size === 0} onClick={onSave} startIcon={<SaveIcon />}>
                        Save
                    </Button>
                    <Button variant="outlined" color="secondary" disabled={edits.size === 0} onClick={onDiscard} startIcon={<UndoIcon />}>
                        Discard
                    </Button>
                </Box>
            </Paper>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={allVisibleSelected}
                                    indeterminate={selected.size > 0 && !allVisibleSelected}
                                    onChange={toggleSelectAll}
                                />
                            </TableCell>
                            <TableCell>POS #</TableCell>
                            <TableCell sx={{ minWidth: "250px" }}>Location</TableCell>
                            <TableCell>Current User</TableCell>
                            <TableCell>Battery</TableCell>
                            <TableCell>Printer</TableCell>
                            <TableCell>Last Checked In</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleDevices.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} sx={{ textAlign: "center" }}>
                                    No devices found
                                </TableCell>
                            </TableRow>
                        )}
                        {visibleDevices.map((it) => {
                            const dirty = edits.has(it.identifier);
                            return (
                                <TableRow
                                    key={`device-${it.identifier}`}
                                    hover
                                    selected={selected.has(it.identifier)}
                                    sx={dirty ? { backgroundColor: "rgba(255, 135, 51, 0.15) !important" } : {}}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox checked={selected.has(it.identifier)} onChange={() => toggleSelected(it.identifier)} />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body1" fontWeight="bold">
                                            {it.identifier}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={dirty ? `Was: ${it.location || "(none)"}` : ""}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                variant="standard"
                                                placeholder="(no location)"
                                                value={edits.get(it.identifier) ?? it.location ?? ""}
                                                onChange={(e) => stageEdit(it.identifier, e.target.value)}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>{it.user}</TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center">
                                            <BatteryIcon fontSize="small" />
                                            {parseInt(it.battery, 10)}%
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={it.printerStatus}
                                            size="small"
                                            sx={{ backgroundColor: printerColorFromStatus(it.printerStatus), color: "black" }}
                                        />
                                    </TableCell>
                                    <TableCell>{it.checkedInAt ? <TimeAgo date={Date.parse(it.checkedInAt)} locale="en-US" /> : "-"}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <DeviceControlsDialog
                target={
                    controlsOpen
                        ? {
                              kind: "group",
                              label: `${selected.size} Selected`,
                              nodeNumbers: Array.from(selected).map((it) => `${it}`),
                              onlineCount: 0,
                          }
                        : null
                }
                onClose={() => setControlsOpen(false)}
            />
        </Container>
    );
});

export default DeviceEditor;
